import React, { useState } from 'react';
import { FileText, Upload, CheckCircle2, Loader2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API_SUFFIX = process.env.REACT_APP_API_SUFFIX || '';

const Devis = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    serviceType: '',
    surface: '',
    city: '',
    zipCode: '',
    contactType: 'company',
    companyName: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    urgency: '',
    message: '',
    photos: null
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      let response;
      
      if (formData.photos && formData.photos.length > 0) {
        // Use multipart form data when photos are attached
        const fd = new FormData();
        fd.append('serviceType', formData.serviceType);
        fd.append('surface', formData.surface);
        fd.append('city', formData.city);
        fd.append('zipCode', formData.zipCode);
        fd.append('contactType', formData.contactType);
        fd.append('companyName', formData.companyName);
        fd.append('firstName', formData.firstName);
        fd.append('lastName', formData.lastName);
        fd.append('phone', formData.phone);
        fd.append('email', formData.email);
        fd.append('urgency', formData.urgency);
        fd.append('message', formData.message);
        for (let i = 0; i < formData.photos.length; i++) {
          fd.append('photos', formData.photos[i]);
        }
        response = await fetch(`${API_URL}/api/devis-with-photos${API_SUFFIX}`, {
          method: 'POST',
          body: fd,
        });
      } else {
        // Use JSON when no photos
        response = await fetch(`${API_URL}/api/devis${API_SUFFIX}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            serviceType: formData.serviceType,
            surface: formData.surface,
            city: formData.city,
            zipCode: formData.zipCode,
            contactType: formData.contactType,
            companyName: formData.companyName,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone,
            email: formData.email,
            urgency: formData.urgency,
            message: formData.message,
          }),
        });
      }

      if (!response.ok) throw new Error('Erreur serveur');
      
      toast({
        title: "Demande envoyée !",
        description: "Nous vous répondrons sous 24h avec un devis détaillé. Un email de confirmation vous a été envoyé.",
      });

      setIsSuccess(true);

      setFormData({
        serviceType: '',
        surface: '',
        city: '',
        zipCode: '',
        contactType: 'company',
        companyName: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        urgency: '',
        message: '',
        photos: null
      });
      // Reset file input
      const fileInput = document.getElementById('photos');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Demande de devis gratuit - Nettoyage par drone | PULVISTAR</title>
        <meta name="description" content="Demandez votre devis gratuit pour le nettoyage par drone de vos toitures, panneaux solaires, façades ou bâtiments. Réponse sous 24h. À partir de 5.90€/m²." />
        <link rel="canonical" href="https://pulvistar.fr/devis" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-400/20 rounded-full blur-3xl"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <Badge className="bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/30 border-0 text-base px-4 py-2">
                <FileText className="w-4 h-4 mr-2" />
                Devis gratuit sous 24h
              </Badge>
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
              Demandez votre devis
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
              Remplissez le formulaire ci-dessous pour recevoir une proposition détaillée et personnalisée
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {isSuccess ? (
              <Card className="p-12 lg:p-16 border-gray-200 shadow-xl text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Outfit", sans-serif' }}>
                  Votre demande de devis a bien été envoyée !
                </h2>
                <p className="text-lg text-gray-600 mb-8" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                  Notre équipe vous répondra sous 24h avec une proposition détaillée et transparente. Un email de confirmation vous a été envoyé.
                </p>
                <Button size="lg" className="gap-2 bg-blue-900 hover:bg-blue-800" onClick={() => setIsSuccess(false)}>
                  Envoyer une autre demande
                </Button>
              </Card>
            ) : (
            <Card className="p-8 lg:p-12 border-gray-200 shadow-xl">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Service Type */}
                <div className="space-y-3">
                  <Label htmlFor="serviceType" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    Type de support à nettoyer *
                  </Label>
                  <Select value={formData.serviceType} onValueChange={(value) => handleChange('serviceType', value)} required>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Sélectionnez un service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="toiture">Toiture (démoussage/entretien)</SelectItem>
                      <SelectItem value="panneaux-solaires">Panneaux solaires</SelectItem>
                      <SelectItem value="eolienne">Éolienne</SelectItem>
                      <SelectItem value="monument">Monument historique</SelectItem>
                      <SelectItem value="batiment-industriel">Bâtiment industriel</SelectItem>
                      <SelectItem value="copropriete">Immeuble copropriété</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Surface & Location */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label htmlFor="surface" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Surface (m²) ou estimation
                    </Label>
                    <Input
                      id="surface"
                      type="text"
                      placeholder="Ex: 200 m²"
                      value={formData.surface}
                      onChange={(e) => handleChange('surface', e.target.value)}
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="zipCode" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Code postal *
                    </Label>
                    <Input
                      id="zipCode"
                      type="text"
                      placeholder="Ex: 75001"
                      value={formData.zipCode}
                      onChange={(e) => handleChange('zipCode', e.target.value)}
                      className="h-12"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="city" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    Ville *
                  </Label>
                  <Input
                    id="city"
                    type="text"
                    placeholder="Ex: Paris"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    className="h-12"
                    required
                  />
                </div>

                {/* Contact Type */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    Vous êtes *
                  </Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="contactType"
                        value="company"
                        checked={formData.contactType === 'company'}
                        onChange={(e) => handleChange('contactType', e.target.value)}
                        className="w-4 h-4"
                      />
                      <span style={{ fontFamily: '"Inter", sans-serif' }}>Entreprise / Collectivité</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="contactType"
                        value="individual"
                        checked={formData.contactType === 'individual'}
                        onChange={(e) => handleChange('contactType', e.target.value)}
                        className="w-4 h-4"
                      />
                      <span style={{ fontFamily: '"Inter", sans-serif' }}>Particulier</span>
                    </label>
                  </div>
                </div>

                {/* Company Name (conditional) */}
                {formData.contactType === 'company' && (
                  <div className="space-y-3">
                    <Label htmlFor="companyName" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Nom de la société / organisme *
                    </Label>
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="Nom de votre entreprise"
                      value={formData.companyName}
                      onChange={(e) => handleChange('companyName', e.target.value)}
                      className="h-12"
                      required
                    />
                  </div>
                )}

                {/* Contact Details */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label htmlFor="firstName" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Prénom *
                    </Label>
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="Votre prénom"
                      value={formData.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      className="h-12"
                      required
                    />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="lastName" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Nom *
                    </Label>
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Votre nom"
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      className="h-12"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label htmlFor="phone" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Téléphone *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="06 12 34 56 78"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="h-12"
                      required
                    />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="email" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="votre.email@exemple.fr"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="h-12"
                      required
                    />
                  </div>
                </div>

                {/* Urgency */}
                <div className="space-y-3">
                  <Label htmlFor="urgency" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    Urgence de l'intervention
                  </Label>
                  <Select value={formData.urgency} onValueChange={(value) => handleChange('urgency', value)}>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Sélectionnez un délai" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Immédiat (sous 48h)</SelectItem>
                      <SelectItem value="week">Sous 7 jours</SelectItem>
                      <SelectItem value="month">Sous 30 jours</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div className="space-y-3">
                  <Label htmlFor="message" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    Décrivez votre projet
                  </Label>
                  <Textarea
                    id="message"
                    placeholder="Détails supplémentaires, contraintes d'accès, questions..."
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className="min-h-32"
                  />
                </div>

                {/* Photo Upload */}
                <div className="space-y-3">
                  <Label htmlFor="photos" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    Photos du site (optionnel)
                  </Label>
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-cyan-500 transition-colors cursor-pointer"
                    onClick={() => document.getElementById('photos').click()}
                  >
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    {formData.photos && formData.photos.length > 0 ? (
                      <p className="text-cyan-600 font-medium" style={{ fontFamily: '"Inter", sans-serif' }}>
                        {formData.photos.length} photo(s) sélectionnée(s)
                      </p>
                    ) : (
                      <>
                        <p className="text-gray-600 mb-2" style={{ fontFamily: '"Inter", sans-serif' }}>
                          Cliquez ici pour ajouter des photos
                        </p>
                        <p className="text-sm text-gray-500" style={{ fontFamily: '"Inter", sans-serif' }}>
                          Formats acceptés: JPG, PNG (max 10 Mo)
                        </p>
                      </>
                    )}
                    <Input
                      id="photos"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleChange('photos', e.target.files)}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <Button type="submit" size="lg" className="w-full gap-2 bg-blue-900 hover:bg-blue-800" disabled={isSubmitting} data-testid="devis-submit-btn">
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande de devis'}
                  </Button>
                  <p className="text-sm text-gray-500 text-center mt-4" style={{ fontFamily: '"Inter", sans-serif' }}>
                    Réponse sous 24h • Devis détaillé et transparent • Sans engagement
                  </p>
                </div>
              </form>
            </Card>
            )}

            {/* Info Cards */}
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <Card className="p-6 text-center border-gray-200">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-7 h-7 text-blue-900" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Outfit", sans-serif' }}>
                  Réponse rapide
                </h3>
                <p className="text-sm text-gray-600" style={{ fontFamily: '"Inter", sans-serif' }}>
                  Devis détaillé sous 24h ouvrées
                </p>
              </Card>
              <Card className="p-6 text-center border-gray-200">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7 text-blue-900" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Outfit", sans-serif' }}>
                  Sans engagement
                </h3>
                <p className="text-sm text-gray-600" style={{ fontFamily: '"Inter", sans-serif' }}>
                  Gratuit et sans obligation
                </p>
              </Card>
              <Card className="p-6 text-center border-gray-200">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7 text-blue-900" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Outfit", sans-serif' }}>
                  Tarif transparent
                </h3>
                <p className="text-sm text-gray-600" style={{ fontFamily: '"Inter", sans-serif' }}>
                  Détail de chaque poste
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Devis;
